import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Users, Plus, Trash2, Loader2, Copy, ShieldAlert, Activity, KeyRound } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { listScimTokens, createScimToken, revokeScimToken } from "@/lib/scim.functions";
import { listScimUsers, listScimGroups, listScimRequestLog, scimStats } from "@/lib/scim-admin.functions";

export const Route = createFileRoute("/_authenticated/app/settings/scim")({
  head: () => ({
    meta: [
      { title: "SCIM provisioning — CairnOS" },
      { name: "description", content: "Manage SCIM tokens, provisioned users, groups, and request log." },
    ],
  }),
  component: ScimAdmin,
});

const ROLES = ["operator", "manager", "tenant_admin", "viewer"] as const;

function StatusBadge({ status }: { status: number }) {
  const cls =
    status >= 500 ? "bg-destructive/10 text-destructive"
    : status >= 400 ? "bg-amber-500/10 text-amber-600"
    : "bg-emerald-500/10 text-emerald-600";
  return <Badge variant="outline" className={`font-mono ${cls}`}>{status}</Badge>;
}

function ScimAdmin() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const tokensFn = useServerFn(listScimTokens);
  const createFn = useServerFn(createScimToken);
  const revokeFn = useServerFn(revokeScimToken);
  const usersFn = useServerFn(listScimUsers);
  const groupsFn = useServerFn(listScimGroups);
  const logFn = useServerFn(listScimRequestLog);
  const statsFn = useServerFn(scimStats);

  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<{ token: string; prefix: string } | null>(null);
  const [logFilter, setLogFilter] = useState<{ tokenId: string; onlyErrors: boolean }>({ tokenId: "all", onlyErrors: false });

  const statsQ = useQuery({
    queryKey: ["scim-stats", tenantId],
    enabled: !!tenantId,
    queryFn: () => statsFn({ data: { tenantId: tenantId! } }),
    refetchInterval: 30_000,
  });
  const tokensQ = useQuery({
    queryKey: ["scim-tokens", tenantId],
    enabled: !!tenantId,
    queryFn: () => tokensFn({ data: { tenantId: tenantId! } }),
  });
  const usersQ = useQuery({
    queryKey: ["scim-users", tenantId],
    enabled: !!tenantId,
    queryFn: () => usersFn({ data: { tenantId: tenantId! } }),
  });
  const groupsQ = useQuery({
    queryKey: ["scim-groups", tenantId],
    enabled: !!tenantId,
    queryFn: () => groupsFn({ data: { tenantId: tenantId! } }),
  });
  const logQ = useQuery({
    queryKey: ["scim-log", tenantId, logFilter],
    enabled: !!tenantId,
    queryFn: () =>
      logFn({
        data: {
          tenantId: tenantId!,
          tokenId: logFilter.tokenId === "all" ? null : logFilter.tokenId,
          onlyErrors: logFilter.onlyErrors,
          limit: 200,
        },
      }),
    refetchInterval: 15_000,
  });

  const createMut = useMutation({
    mutationFn: (p: { label: string; defaultRole: string }) =>
      createFn({ data: { tenantId: tenantId!, label: p.label, defaultRole: p.defaultRole as any } }),
    onSuccess: (res) => {
      setRevealed({ token: res.token, prefix: res.prefix });
      setCreateOpen(false);
      void qc.invalidateQueries({ queryKey: ["scim-tokens", tenantId] });
      void qc.invalidateQueries({ queryKey: ["scim-stats", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Token revoked");
      void qc.invalidateQueries({ queryKey: ["scim-tokens", tenantId] });
      void qc.invalidateQueries({ queryKey: ["scim-stats", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const tokens = tokensQ.data?.tokens ?? [];
  const stats = statsQ.data;

  const scimUrl =
    typeof window !== "undefined" ? `${window.location.origin}/api/public/scim/v2` : "";

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" /> SCIM provisioning
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Connect Okta, Entra ID, JumpCloud, or any SCIM v2-compliant IdP to provision users
            and groups automatically.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
            <Stat label="Users" value={stats?.users ?? 0} sub={`${stats?.activeUsers ?? 0} active`} />
            <Stat label="Groups" value={stats?.groups ?? 0} />
            <Stat label="Active tokens" value={stats?.activeTokens ?? 0} />
            <Stat label="Requests 24h" value={stats?.requests24h ?? 0} />
            <Stat label="Errors 24h" value={stats?.errors24h ?? 0} tone={stats?.errors24h ? "warn" : undefined} />
            <Stat label="Endpoint" value="v2" sub="OK" tone="ok" />
          </div>
          <div className="mt-4 text-xs border-t pt-3 flex items-center gap-2 text-muted-foreground">
            <span>SCIM endpoint:</span>
            <code className="font-mono">{scimUrl}</code>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => { navigator.clipboard.writeText(scimUrl); toast.success("Copied"); }}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="tokens">
        <TabsList>
          <TabsTrigger value="tokens"><KeyRound className="h-3.5 w-3.5 mr-1" /> Tokens</TabsTrigger>
          <TabsTrigger value="users"><Users className="h-3.5 w-3.5 mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="groups">Groups</TabsTrigger>
          <TabsTrigger value="log"><Activity className="h-3.5 w-3.5 mr-1" /> Request log</TabsTrigger>
        </TabsList>

        <TabsContent value="tokens">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle>SCIM Tokens</CardTitle>
                <CardDescription>Bearer tokens your IdP uses to authenticate provisioning calls.</CardDescription>
              </div>
              <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-2" /> New token</Button>
            </CardHeader>
            <CardContent>
              {tokensQ.isLoading ? (
                <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
              ) : tokens.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-6">No tokens issued yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Label</TableHead>
                      <TableHead>Prefix</TableHead>
                      <TableHead>Default role</TableHead>
                      <TableHead>Rate/min</TableHead>
                      <TableHead>Last used</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tokens.map((t: any) => (
                      <TableRow key={t.id}>
                        <TableCell className="font-medium">{t.label}</TableCell>
                        <TableCell className="font-mono text-xs">{t.token_prefix}…</TableCell>
                        <TableCell><Badge variant="secondary">{t.default_role}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{t.rate_limit_per_minute}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {t.last_used_at ? formatDistanceToNow(new Date(t.last_used_at), { addSuffix: true }) : "never"}
                        </TableCell>
                        <TableCell>
                          {t.revoked_at
                            ? <Badge variant="outline" className="bg-destructive/10 text-destructive">revoked</Badge>
                            : <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600">active</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          {!t.revoked_at && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { if (confirm(`Revoke "${t.label}"? Your IdP will stop syncing immediately.`)) revokeMut.mutate(t.id); }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>Provisioned Users</CardTitle>
              <CardDescription>Users created by your IdP via SCIM (read-only — manage in your IdP).</CardDescription>
            </CardHeader>
            <CardContent>
              {usersQ.isLoading ? (
                <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
              ) : (usersQ.data?.rows ?? []).length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-6">No SCIM-provisioned users yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Username</TableHead>
                      <TableHead>External ID</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(usersQ.data?.rows ?? []).map((u: any) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.username}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{u.external_id}</TableCell>
                        <TableCell>
                          {u.active
                            ? <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600">active</Badge>
                            : <Badge variant="outline" className="bg-muted">deactivated</Badge>}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(u.created_at), { addSuffix: true })}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(u.updated_at), { addSuffix: true })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="groups">
          <Card>
            <CardHeader>
              <CardTitle>Provisioned Groups</CardTitle>
              <CardDescription>Groups synced from your IdP, with current member counts.</CardDescription>
            </CardHeader>
            <CardContent>
              {groupsQ.isLoading ? (
                <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
              ) : (groupsQ.data?.rows ?? []).length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-6">No SCIM-provisioned groups yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Display name</TableHead>
                      <TableHead>External ID</TableHead>
                      <TableHead>Members</TableHead>
                      <TableHead>Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(groupsQ.data?.rows ?? []).map((g: any) => (
                      <TableRow key={g.id}>
                        <TableCell className="font-medium">{g.display_name}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{g.external_id}</TableCell>
                        <TableCell>{g.memberCount}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(g.updated_at), { addSuffix: true })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="log">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle>Request Log</CardTitle>
                <CardDescription>Recent SCIM API calls. Use to debug your IdP integration.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={logFilter.tokenId}
                  onValueChange={(v) => setLogFilter((p) => ({ ...p, tokenId: v }))}
                >
                  <SelectTrigger className="w-[200px]"><SelectValue placeholder="All tokens" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tokens</SelectItem>
                    {tokens.map((t: any) => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2 text-xs">
                  <Switch
                    checked={logFilter.onlyErrors}
                    onCheckedChange={(c) => setLogFilter((p) => ({ ...p, onlyErrors: c }))}
                  />
                  Errors only
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {logQ.isLoading ? (
                <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
              ) : (logQ.data?.rows ?? []).length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-6">No requests yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Path</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Remote IP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(logQ.data?.rows ?? []).map((r: any) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{r.method}</TableCell>
                        <TableCell className="font-mono text-xs max-w-[260px] truncate">{r.path}</TableCell>
                        <TableCell><StatusBadge status={r.status} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.duration_ms ?? "—"}ms</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{r.remote_ip ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CreateTokenDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(p) => createMut.mutate(p)}
        submitting={createMut.isPending}
      />

      <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" /> Save this token now
            </DialogTitle>
            <DialogDescription>
              Paste it into your IdP's SCIM bearer-token field. It will not be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 bg-muted p-3 rounded font-mono text-xs break-all">
            {revealed?.token}
            <Button
              size="icon" variant="ghost"
              onClick={() => { navigator.clipboard.writeText(revealed?.token ?? ""); toast.success("Copied"); }}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealed(null)}>I've saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: any; sub?: string; tone?: "ok" | "warn" }) {
  const cls = tone === "warn" ? "text-amber-600" : tone === "ok" ? "text-emerald-600" : "";
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-xl font-semibold ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function CreateTokenDialog({
  open, onClose, onSubmit, submitting,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (p: { label: string; defaultRole: string }) => void;
  submitting: boolean;
}) {
  const [label, setLabel] = useState("");
  const [defaultRole, setDefaultRole] = useState<string>("operator");

  const submit = () => {
    if (label.trim().length < 2) return toast.error("Label required");
    onSubmit({ label: label.trim(), defaultRole });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New SCIM token</DialogTitle>
          <DialogDescription>
            Newly provisioned users will be created with the selected default role. Change individual roles via SCIM groups.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="scim-label">Label</Label>
            <Input id="scim-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Okta Production" />
          </div>
          <div>
            <Label htmlFor="scim-role">Default role</Label>
            <Select value={defaultRole} onValueChange={setDefaultRole}>
              <SelectTrigger id="scim-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}